import React, { useState } from 'react'
import { centre, fmt, idx, logPosition, rankingRows, METRICS } from './benchmarkMath'
import './ranking.css'

/**
 * Channel ranking — React port of benchmark-mock/render/view-ranking.js.
 * One channel at a time (own tab state); log-scale bars against the channel benchmark band.
 */

// 'Toyota · rank 3 of 41 · x1.23 vs mean' — or 'no delivery on X' / 'outside Tier k'.
function summaryText(model, partner, channel, rows, bench, { scope, metric, stat }) {
    const selected = model.rows.find(row => row.partner === partner && row.channel === channel);
    if (!selected) return partner + ' · no delivery on ' + channel;
    const rank = rows.findIndex(row => row.partner === partner);
    let summary = partner + ' · ' + (rank >= 0 ? 'rank ' + (rank + 1) + ' of ' + rows.length : 'outside Tier ' + scope.slice(-1));
    const ratio = bench ? idx(selected[metric], centre(bench, stat)) : null;
    if (ratio !== null) summary += ' · x' + ratio.toFixed(2) + ' vs ' + stat;
    return summary;
}

function barColour(isSelected, bench, value, stat) {
    if (isSelected) return 'var(--ink)';
    if (!bench) return 'var(--silver)';
    return value >= centre(bench, stat) ? 'var(--ok)' : 'var(--alert)';
}

export default function Ranking({ model, metric, scope, stat, partners }) {
    const [tab, setTab] = useState('TV');
    // 'TV' default; falls back to the first channel when the current data has no such channel
    const channel = model.channels.includes(tab) ? tab : model.channels[0];
    const rows = rankingRows(model, channel, scope, metric);
    const bench = model.bench[channel][scope][metric];
    const selectedPartners = new Set(partners);
    const max = rows.length ? rows[0][metric] : 0;
    const position = value => logPosition(value, max);
    const statLabel = stat[0].toUpperCase() + stat.slice(1);

    return (
        <React.Fragment>
            {/* stable keys keep the clicked tab's DOM node, so it keeps focus across re-renders */}
            <div className="seg ranking-tabs" role="group" aria-label="Ranking channel">
                {model.channels.map(name => (
                    <button type="button" key={name} className={name === channel ? 'on' : ''} data-channel={name}
                        aria-pressed={String(name === channel)} onClick={() => setTab(name)}>{name}</button>
                ))}
            </div>
            {partners.map(partner => (
                <p className="ranking-summary" aria-live="polite" key={partner}>
                    {summaryText(model, partner, channel, rows, bench, { scope, metric, stat })}
                </p>
            ))}
            <div className="ranking-key">
                <span className="ranking-scale">Log scale · {rows.length} partners</span>
                {bench ? (
                    <React.Fragment>
                        <span className="ranking-median-key">{statLabel} {fmt(metric, centre(bench, stat))}</span>
                        <span className="ranking-band-key">Middle 50% {fmt(metric, bench.p25)}–{fmt(metric, bench.p75)}</span>
                    </React.Fragment>
                ) : (
                    <span className="ranking-small">n too small for a benchmark</span>
                )}
            </div>
            <div className="ranking-body" role="list" aria-label={channel + ' partners ranked by ' + METRICS[metric]}>
                {bench && rows.length ? (
                    <div className="ranking-overlays" aria-hidden="true">
                        <div className="ranking-band"
                            style={{ left: position(bench.p25) + '%', width: position(bench.p75) - position(bench.p25) + '%' }} />
                        <div className="ranking-median" style={{ left: position(centre(bench, stat)) + '%' }} />
                    </div>
                ) : null}
                {rows.map((row, index) => {
                    const isSelected = selectedPartners.has(row.partner);
                    const value = fmt(metric, row[metric]);
                    return (
                        <div className={'ranking-row' + (isSelected ? ' selected' : '')} role="listitem" key={row.partner}
                            data-partner={row.partner} title={'Rank ' + (index + 1) + ' · ' + row.partner + ' · ' + value}>
                            <span className="ranking-name">{row.partner}</span>
                            <div className="ranking-track" aria-hidden="true">
                                <div className={'bar' + (isSelected ? ' selected' : '')} style={{
                                    width: position(row[metric]) + '%',
                                    background: barColour(isSelected, bench, row[metric], stat)
                                }} />
                            </div>
                            <span className="ranking-value num">{value}</span>
                        </div>
                    );
                })}
                {rows.length ? null : <p className="muted">No delivery on {channel} in this scope.</p>}
            </div>
        </React.Fragment>
    );
}
